import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Brush } from "@/components/brand/Lettering";
import { useCart } from "@/context/CartContext";
import { getWall, monthLabel } from "@/lib/api";
import { cadShort } from "@/lib/site";

const ROT = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2"];
const TAPE = ["bg-lime/80", "bg-pink/80", "bg-sky/80"];

const WallCard = ({ w, i }) => {
  const { addItem } = useCart();
  const [flip, setFlip] = useState(false);
  const [size, setSize] = useState("M");
  const p = w.product;
  return (
    <article className={`relative mb-10 break-inside-avoid ${ROT[i % 4]}`} data-testid={`wall-card-${w.slug}`}>
      <span className={`absolute -top-3 left-1/2 z-10 h-7 w-24 -translate-x-1/2 rotate-[-4deg] ${TAPE[i % 3]}`} aria-hidden="true" />
      <div className="rounded-[18px] border-[1.5px] border-ink bg-white p-3 pb-4 shadow-[6px_6px_0_#111]">
        <button onClick={() => setFlip(!flip)} onMouseEnter={() => setFlip(true)} onMouseLeave={() => setFlip(false)} className="flip-card block aspect-square w-full" aria-label={`Show ${w.first_name}'s story`} data-testid={`wall-flip-${w.slug}`}>
          <div className={`flip-inner ${flip ? "is-flipped" : ""}`}>
            <div className="flip-face tile"><img src={w.photo_url || w.design_url} alt={`${w.design_title} by ${w.first_name}`} loading="lazy" className="h-full w-full object-cover mix-blend-multiply" /></div>
            <div className="flip-face flip-back flex flex-col justify-between rounded-[16px] bg-pink p-5 text-left">
              <p className="font-a text-[15px] leading-relaxed" data-testid={`wall-story-${w.slug}`}>"{w.story.slice(0, 180)}{w.story.length > 180 ? "…" : ""}"</p>
              <img src={w.design_url} alt="" className="h-20 w-20 self-end rounded-[12px] border border-ink object-cover" />
            </div>
          </div>
        </button>
        <Brush lines={[monthLabel(w.month)]} halo={false} className="mt-4 text-3xl" />
        <p className="display mt-2 text-3xl">{w.design_title}</p>
        <p className="font-a mt-1 text-[14px] text-ink/70">{w.first_name}, {w.age} · {w.city}</p>
        {p && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-ink/10 pt-3">
            <img src={p.image} alt="" className="h-10 w-10 rounded-[8px] object-cover" />
            <span className="font-a text-sm">{cadShort(p.price)}</span>
            <select value={size} onChange={(e) => setSize(e.target.value)} className="ml-auto rounded-[8px] border border-ink/30 px-1.5 py-1 font-mono text-xs" aria-label="Size" data-testid={`wall-size-${w.slug}`}>{Object.keys(p.sizes).map((s) => <option key={s}>{s}</option>)}</select>
            <button onClick={() => addItem(p, { color: p.colors[0].name, size, qty: 1 })} className="btn btn-lime !h-8 !px-3 !text-[10px]" data-testid={`wall-add-${w.slug}`}>Add</button>
            <Link to={`/product/${p.slug}`} className="link-u w-full !text-[10px]" data-testid={`wall-tee-${w.slug}`}>View tee <ArrowRight size={12} /></Link>
          </div>
        )}
      </div>
    </article>
  );
};

export const WinnersWall = ({ testid = "winners-wall" }) => {
  const [rows, setRows] = useState(null);
  const [year, setYear] = useState("all");
  useEffect(() => { getWall().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const years = [...new Set(rows.map((w) => w.month.slice(0, 4)))];
  const shown = rows.filter((w) => year === "all" || w.month.startsWith(year));
  return (
    <section data-testid={testid} aria-labelledby={`${testid}-title`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 id={`${testid}-title`} className="display text-[clamp(3rem,7vw,6rem)]">Wall of <span className="hl hl-lime hl-thick">sus</span></h2>
        <div className="flex gap-2" role="group" aria-label="Filter by year">
          {["all", ...years].map((y) => <button key={y} onClick={() => setYear(y)} aria-pressed={year === y} className={`label rounded-full border px-4 py-2 font-bold ${year === y ? "border-ink bg-ink text-cloud" : "border-ink/30"}`} data-testid={`${testid}-year-${y}`}>{y === "all" ? "All" : y}</button>)}
        </div>
      </div>
      <div className="mt-12 columns-1 gap-8 sm:columns-2 lg:columns-3 xl:columns-4" data-testid={`${testid}-grid`}>
        {shown.map((w, i) => <WallCard key={w.id} w={w} i={i} />)}
      </div>
    </section>
  );
};
